import { EmailResultCard } from './EmailResultCard';
import { EmailDraftCard } from './EmailDraftCard';
import { NoteResultCard } from './NoteResultCard';
import { DocResultCard } from './DocResultCard';
import { ContactsGridCard } from './ContactsGridCard';
import { ContactDetailCard } from './ContactDetailCard';
import { PlaceResultCard } from './PlaceResultCard';
import { TaskResultCard } from './TaskResultCard';
import { EventsResultCard } from './EventsResultCard';
import { InboxResultCard } from './InboxResultCard';
import { InboxTriageCard } from './InboxTriageCard';
import { TravelResultCard } from './TravelResultCard';
import { ReservationsResultCard } from './ReservationsResultCard';
import { CircleResultCard } from './CircleResultCard';
import { PersonaResultCard } from './PersonaResultCard';
import { SocialResultCard } from './SocialResultCard';

import { BriefingResultCard } from './BriefingResultCard';
import { ReportResultCard } from './ReportResultCard';
import { calendarFlash } from '@/lib/calendarFlash';
import type {
  ContactCardData,
  DocCardData,
  EmailCardData,
  EmailDraftCardData,
  EventsCardData,
  InboxCardData,
  InboxTriageCardData,
  NoteCardData,
  PlaceCardData,
  ReportCardData,
  ResultCard,
  ResultCardKind,
  TaskCardData,
} from './types';
import './result-cards.css';

/**
 * Renders a list of result cards beneath an assistant message. Switches on
 * card.kind. `email` opens a thread; `email_draft` is an editable send card;
 * `note`/`place` open their object; `contact` is a clickable grid; `task` is a
 * self-contained interactive checklist (no onCardAction path — it writes back
 * locally via useUpdateObject).
 */
export function ResultCardRenderer({
  cards,
  onCardAction,
}: {
  cards: ResultCard[];
  onCardAction: (
    a: { kind: ResultCardKind; intent: 'open' | 'send' | 'compose'; data: any },
  ) => void | Promise<boolean | { subject?: string; body?: string } | null | void>;
}) {
  if (!cards.length) return null;

  return (
    <div className="rc-list">
      {cards.map((card, i) => {
        switch (card.kind) {
          case 'email':
            return (
              <EmailResultCard
                key={i}
                data={card.data as unknown as EmailCardData}
                onOpen={() => onCardAction({ kind: 'email', intent: 'open', data: card.data })}
              />
            );
          case 'email_draft': {
            const draftData = card.data as unknown as EmailDraftCardData;
            return (
              <EmailDraftCard
                key={i}
                data={draftData}
                onSend={async (edited) => {
                  const r = await onCardAction({ kind: 'email_draft', intent: 'send', data: edited });
                  return typeof r === 'boolean' ? r : undefined;
                }}
                onCompose={async (args) => {
                  const r = await onCardAction({
                    kind: 'email_draft',
                    intent: 'compose',
                    data: { ...args, contactId: draftData.contactId, actionType: draftData.actionType },
                  });
                  return r && typeof r === 'object' ? r : undefined;
                }}
                /* args carries { style, rewriteFrom, avoidBody } */
              />
            );
          }
          case 'note':
            return (
              <NoteResultCard
                key={i}
                data={card.data as unknown as NoteCardData}
                onOpen={() => onCardAction({ kind: 'note', intent: 'open', data: card.data })}
              />
            );
          case 'doc':
            return (
              <DocResultCard
                key={i}
                data={card.data as unknown as DocCardData}
                onOpen={() => onCardAction({ kind: 'doc', intent: 'open', data: card.data })}
              />
            );
          case 'contact': {
            const contactData = card.data as unknown as ContactCardData;
            // One match → a rich detail card; several → the compact grid.
            if (contactData.contacts?.length === 1) {
              return (
                <ContactDetailCard
                  key={i}
                  contact={contactData.contacts[0]}
                  onOpen={() => onCardAction({ kind: 'contact', intent: 'open', data: { id: contactData.contacts[0].id } })}
                />
              );
            }
            return (
              <ContactsGridCard
                key={i}
                data={contactData}
                onOpenContact={(id) => onCardAction({ kind: 'contact', intent: 'open', data: { id } })}
              />
            );
          }
          case 'place':
            return (
              <PlaceResultCard
                key={i}
                data={card.data as unknown as PlaceCardData}
                onOpen={() => onCardAction({ kind: 'place', intent: 'open', data: card.data })}
              />
            );
          case 'task':
            return <TaskResultCard key={i} data={card.data as unknown as TaskCardData} />;
          case 'report':
            return (
              <ReportResultCard
                key={i}
                data={card.data as unknown as ReportCardData}
                onOpen={() => onCardAction({ kind: 'report', intent: 'open', data: card.data })}
              />
            );
          case 'today':
          case 'briefing': // legacy kind from older persisted messages
            return (
              <BriefingResultCard
                key={i}
                data={card.data as Record<string, unknown>}
                onOpenEvent={(_id, start) => {
                  // Flash the event's day red + jump the calendar to month view
                  // (calendarFlash drives useCalendarNavigation → 'month' + the
                  // red day in MonthView). Use the LOCAL date so it matches the
                  // day cell. Then open the calendar module (no eventId, so we
                  // don't trigger the day-view focus that would override 'month').
                  if (start) {
                    const d = new Date(start);
                    if (!Number.isNaN(d.getTime())) {
                      calendarFlash.request(
                        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
                      );
                    }
                  }
                  onCardAction({ kind: 'events', intent: 'open', data: {} });
                }}
              />
            );
          case 'inbox':
            return (
              <InboxResultCard
                key={i}
                data={card.data as unknown as InboxCardData}
                onCardAction={onCardAction}
              />
            );
          case 'travel':
            return <TravelResultCard key={i} />;
          case 'reservations':
            return <ReservationsResultCard key={i} />;
          case 'circle':
            return <CircleResultCard key={i} />;
          case 'persona':
            return <PersonaResultCard key={i} />;
          case 'social':
            return <SocialResultCard key={i} />;

          case 'inbox_triage':
            return (
              <InboxTriageCard
                key={i}
                data={card.data as unknown as InboxTriageCardData}
                onOpenEmail={(row) =>
                  onCardAction({ kind: 'email', intent: 'open', data: { threadId: row.threadId, messageId: row.messageId, integrationId: row.integrationId } })
                }
                onOpenInbox={() => onCardAction({ kind: 'email', intent: 'open', data: {} })}
              />
            );
          case 'events': {
            const eventsData = card.data as unknown as EventsCardData;
            return (
              <EventsResultCard
                key={i}
                data={eventsData}
                onOpenEvent={(e) => onCardAction({ kind: 'events', intent: 'open', data: { eventId: e.id, start: e.start } })}
                onOpenRange={() => onCardAction({ kind: 'events', intent: 'open', data: { rangeStart: eventsData.rangeStart, rangeEnd: eventsData.rangeEnd } })}
              />
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}
